/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 7 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  RefObject,
  type ComponentProps,
  type MouseEvent,
} from "react";
import type { AutocompleteRenderInputParams } from "@mui/material";
import { getViewerUrl } from "@Helpers";
import { defaultSignatureModalState } from "./ReviewsConstants";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import Text from "@CubeComponents/html/Text/Text";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import _ from "lodash";
import { portalCreateRoot } from "@CubeComponents/html/cellPortals";
import {
  fileStatusHelper,
  getIssuesInReviewToolbar,
  getStatusBtnStyle,
  removeResourcesFromReview,
  addResourcesToReview,
  getResourcesInReview,
} from "./ReviewsHelper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { fetchListOfStatusesAvailableRequest } from "@Features/reviews/api/reviews.api";
import { getResourcesInReviewUrl } from "@Services/dataManager";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import OpenIssueInViewerIcon from "@Assets/icons/OpenInViewer.svg";
import ChangeFileStatusModal from "./ChangeFileStatusModal";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { CreateIssueModal, IssueCard, IssueDetails, IssuesGrid, ViewIssue } from "@Features/issues";
import ResourceStatusHistoryInReview from "./ResourceStatusHistoryInReview";
import { setModalStatus } from "../../../../reducers/Viewer";
import IconComponent from "@CubeComponents/icons";
import { getIssuesData, removeIssueFromForm, addFormIdToIssues } from "@Features/issues/state/bcfData.actions";
import { emptyIssuesData } from "@Features/issues/state/issues";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import ExportPdfOptionsModal from "@CubeComponents/form/PaperSizeSelector/ExportPdfOptionsModal";
import type { PaperSize } from "@CubeComponents/form/PaperSizeSelector/PaperSizeSelector";
import { htmlToText } from "@Services/bcfConverter";
import { getImageUrl } from "@Utils";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import { RootState } from "@Reducers/index";
import type {
  FetchListOfStatusesAvailableRequest,
  GetResourcesInReviewResponse,
  ReviewDataResult,
  ReviewStatusItem,
} from "@ServerTypes/reviews/reviews.schema";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ReviewApprovalStage, ReviewResourceDescriptor } from "./ReviewsHelper";
import type { Status } from "./reviews.interfaces";
import type { ResourceStatusEntry } from "./ResourceStatusHistoryInReview";
import type {
  ColumnDefinition,
  GridRowBase,
  SlickGrid1Ref,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

/** One element of `getResourcesInReviewResponse.result`. */
type ResourceInReviewItem = NonNullable<GetResourcesInReviewResponse["result"]>[number];

/**
 * SERVER SCHEMA GAP, reported: `resourceInReviewItemSchema.details` declares only
 * title/officialResponse/fileName/severity/fileVersionNumber, and `officialResponse` is
 * `z.unknown()`. This grid also reads `details.fileVersionId` (to open the issue in the viewer) and
 * `details.officialResponse.title`. The schema is `.passthrough()`, so both really arrive — they are
 * simply undeclared. Narrowed here to what the grid reads; not fixed (packages/server is off-limits).
 */
type IssueInReviewDetails = ResourceInReviewItem["details"] & {
  fileVersionId?: ObjectIdString;
  officialResponse?: { title?: string };
};

/**
 * One row of the issues-in-review grid.
 * SERVER SCHEMA GAP, reported: `resourceInReviewItemSchema` declares neither `issueId` nor
 * `resourceType`, yet the service returns both and this grid's `idMapping="issueId"` depends on
 * `issueId` (CUBE-21933).
 */
export type IssueInReviewRow = ResourceInReviewItem &
  GridRowBase & {
    issueId?: ObjectIdString;
    resourceType?: string;
    details: IssueInReviewDetails;
  };

/**
 * The status objects this grid hands to `ChangeFileStatusModal`. `Status` (reviews.interfaces) rather
 * than the server's `reviewStatusItemSchema` because the "no previous status" sentinel this file
 * builds is `{ _id: null, name: null, category: null }` — nulls the server schema cannot express.
 */
type IssueStatusSelection = Partial<Status>;

interface IssueSignatureModalState {
  show: boolean;
  reviewId?: string | null;
  statusTo?: IssueStatusSelection;
  /** `ChangeFileStatusModal` declares `statusFrom?: Status | null`; null is a real value here. */
  statusFrom?: IssueStatusSelection | null;
  resources?: ReviewResourceDescriptor[];
  type?: string;
  requireMandatoryComment?: boolean;
}

interface AddIssueModalState {
  show: boolean;
  /**
   * Two shapes, chosen by `viewingIssuesThroughIssueCards`: resource descriptors on the
   * reviews path (sent to `addResourcesToReview`), raw picked issues on the forms path.
   */
  selectedIssues: ReviewResourceDescriptor[] | IssueItem[];
  type: string;
  newIssueDetails: Record<string, unknown>;
  issueIds?: Array<IssueItem["id"]>;
}

/** `IssueDetails.rowData` element, as this grid assembles it. */
interface IssuePropertiesRow {
  id?: ObjectIdString;
  spaceId: ObjectIdString;
  fileVersionId?: ObjectIdString;
  projectUniqueId: string;
}

interface IssuesInReviewProps {
  /** Reviews `_id` — `objectIdString` in every reviews.schema.ts request this grid issues. */
  reviewId: ObjectIdString;
  spaceId: ObjectIdString;
  projectId: string;
  showProperties?: boolean;
  refreshData: (arg1: boolean, arg2: boolean, arg3: boolean) => void;
  formId?: ObjectIdString;
  disableAddIssues?: boolean;
}

const IssuesInReview = forwardRef<SlickGrid1Ref<IssueInReviewRow>, IssuesInReviewProps>(
  ({ reviewId, spaceId, projectId, showProperties = false, refreshData, formId, disableAddIssues }, ref) => {
    const { t } = useTranslation();
    const dataGrid = ref as RefObject<SlickGrid1Ref<IssueInReviewRow>>;
    const viewingIssuesThroughIssueCards = !!formId;
    const showIssuesInTabularForm = !formId;
    const dispatch = useDispatch();
    const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);

    const reviewData = useSelector((state: RootState) => state.reviewData) as ReviewDataResult & {
      approvalStages: NonNullable<ReviewDataResult["approvalStages"]>;
      reviewStatus: NonNullable<ReviewDataResult["reviewStatus"]>;
      reviewFlow: NonNullable<ReviewDataResult["reviewFlow"]>;
    };

    const defaultAddIssueState: AddIssueModalState = {
      show: false,
      selectedIssues: [],
      type: "create_standalone_issue",
      newIssueDetails: {},
    };

    const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);
    const [addIssueModal, setAddIssueModal] = useState<AddIssueModalState>(defaultAddIssueState);
    const [statusHistoryModal, setStatusHistoryModal] = useState<{
      show: boolean;
      statuses?: ResourceStatusEntry[];
    }>({
      show: false,
    });
    const [signatureModal, setSignatureModal] = useState<IssueSignatureModalState>(defaultSignatureModalState);
    const [issueProperties, setIssueProperties] = useState<{
      show: boolean;
      data: IssuePropertiesRow[] | null;
    }>({
      show: false,
      data: null,
    });
    const [removeIssueModal, setRemoveIssueModal] = useState<{
      show: boolean;
      resources: ReviewResourceDescriptor[];
    }>({
      show: false,
      resources: [],
    });
    const [disabledIssueIdsInForms, setDisabledIssueIdsInForms] = useState<string[]>([]);
    const [selectedIssue, setSelectedIssue] = useState<IssueItem | null | undefined>(null);
    // KNOWN BUG, preserved verbatim: the initial value is the empty ARRAY `[]`, but every read treats
    // this as a `{ [issueId]: true }` map (`Object.keys`, object spread, `IssueCard.selectedIssues`).
    // `Object.keys([])` is `[]` so it behaves, and the first `handleSelectedIssues` call replaces it
    // with a real object. The union is the honest type; the cast at `IssueCard` below is erased.
    const [selectedIssuesInCards, setSelectedIssuesInCards] = useState<Record<string, boolean> | never[]>([]);
    const [issuesAttachedToForm, setIssuesAttachedToForm] = useState<string | undefined>(undefined);
    const [isLoadingAttachIssues, setIsLoadingAttachIssues] = useState(false);
    const [isLoadingRemoveIssues, setIsLoadingRemoveIssues] = useState(false);
    const [showExportPdfOptions, setShowExportPdfOptions] = useState(false);
    const [propertiesBtnDisabled, setPropertiesBtnDisabled] = useState<boolean>(true);
    const [statusOptionsCache, setStatusOptionsCache] = useState<Record<string, ReviewStatusItem[]>>({});

    const issues = useSelector((state: RootState) => state.issues);

    const issuesQuery = useMemo(
      () => ({
        params: {
          resourceTypes: ["RESOURCE_TYPE_ISSUE"],
          reviewId,
        },
      }),
      [reviewId],
    );

    const menuItems = useMemo(
      () => [
        {
          id: "1",
          text: `${viewingIssuesThroughIssueCards ? t("Attach Issue") : t("Add Issue From File")} `,
        },
        {
          id: "2",
          text: `${viewingIssuesThroughIssueCards ? t("Create Issue") : t("Create Standalone Issue")} `,
        },
      ],
      [viewingIssuesThroughIssueCards],
    );

    const closeSignatureModal = (shouldRefreshGrid: boolean = false) => {
      if (shouldRefreshGrid) {
        refreshData(false, true, false);
      }

      setSignatureModal(defaultSignatureModalState);
    };

    const disableIssueIdsInAttachGrid = () => {
      const issueIds = Object.keys(issues);
      setDisabledIssueIdsInForms(issueIds);
    };
    const issuesCount = Object.keys(issues).length;
    useEffect(() => {
      disableIssueIdsInAttachGrid();
    }, [issuesCount]);
    useEffect(() => {
      dispatch(emptyIssuesData());
    }, []);

    const fetchIssuesDataInForm = useCallback(async () => {
      if (viewingIssuesThroughIssueCards) {
        dispatch(emptyIssuesData());

        const params = {
          formId: [formId],
          value: {
            take: 9999,
            requiresCounts: true,
          },
        };
        dispatch(getIssuesData(params));
      }
    }, [viewingIssuesThroughIssueCards, formId]);

    useEffect(() => {
      fetchIssuesDataInForm();
      handleSelectedIssues("removeAll", true);
    }, []);

    const issueStatusChanged = (
      newValue: ReviewStatusItem,
      previousStatus: IssueStatusSelection | undefined,
      // Branded: `resourceSchema.issueId` is `objectIdString`; callers read it off a grid row that
      // already declares it `ObjectIdString`.
      issueId: ObjectIdString,
      resourceType: string,
    ) => {
      const resources = [{ issueId, resourceType }];
      const statusTo = newValue;
      const statusFrom = previousStatus || {
        _id: null,
        name: null,
        category: null,
      };

      const { approvalStages } = reviewData;
      const currentStage = approvalStages.find((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");
      const requireMandatoryComment = currentStage
        ? currentStage.requireMandatoryComment?.enabled && currentStage.requireMandatoryComment?.values?.includes(4)
        : false;

      setSignatureModal({
        show: true,
        reviewId,
        statusTo,
        statusFrom,
        resources,
        type: "standardTransition",
        requireMandatoryComment,
      });
    };

    const fetchStatusOptionsForIssue = async (issueId: string, resource: ReviewResourceDescriptor) => {
      if (statusOptionsCache[issueId]) return statusOptionsCache[issueId];
      try {
        // Brand boundary: `fileStatusHelper` yields the schema's INPUT shape (plain id strings) while
        // `FetchListOfStatusesAvailableRequest` is the `z.infer` output shape (branded ObjectIdString).
        // The assertion is erased; the wire bytes are unchanged.
        const response = await fetchListOfStatusesAvailableRequest({
          value: { params: { reviewId, resource } },
        } as FetchListOfStatusesAvailableRequest);
        if (response.data?.result) {
          const statusOptions = response.data.result;
          setStatusOptionsCache((prev) => ({ ...prev, [issueId]: statusOptions }));
          return statusOptions;
        }
      } catch (e) {
        console.error("Error fetching status options:", e);
        Promise.reject(e);
      }
      return [];
    };

    const selectItem = (e: { item: { id?: string | number } }) => {
      if (e.item.id === "1" || e.item.id === 1) {
        addIssuesInReviewCallback();
      }

      if (e.item.id === "2" || e.item.id === 2) {
        createStandaloneIssue();
      }
    };

    const addBtnTemplate = () => (
      <div className="toolbarButtomCustom">
        <SelectDropDown
          disabled={disableAddIssues}
          select={selectItem}
          buttonType="dropdownBackgroundButton"
          items={menuItems}
        >
          <ButtonComponent>{`+ ${t("Create")}`}</ButtonComponent>
        </SelectDropDown>
      </div>
    );

    const removeBtnTemplateInIssueCards = () => (
      <div className="me-[1.875rem] bg-transparent">
        <ButtonComponent
          disabled={disableAddIssues}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          id="remove_issue"
          onClick={removeIssuesFromCards}
        >
          <span className="flex flex-row items-center justify-start">
            <DeleteIcon size="15" className="me-1" />
            <Text>{t("Remove")}</Text>
          </span>
        </ButtonComponent>
      </div>
    );

    const removeIssuesFromCards = async () => {
      if (!formId) return;
      handleSelectedIssues("removeAll", true);
      // `selectedIssuesInCards` is an untyped selection map and Object.keys() erases key types, so
      // the issue ids enter the type system here, at their declaration.
      const ids = Object.keys(selectedIssuesInCards) as ObjectIdString[];
      dispatch(removeIssueFromForm(ids, formId));
    };

    const removeBtnTemplate = () => (
      <div className="me-[1.875rem] bg-transparent">
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="remove_issue">
          <span className="flex flex-row items-center justify-start">
            <DeleteIcon size="15" className="me-1" />
            <Text>{t("Remove")}</Text>
          </span>
        </ButtonComponent>
      </div>
    );

    const exportBtnTemplate = () => (
      <div className="me-[1.875rem] bg-transparent">
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="excel_export">
          <span className="flex flex-row items-center justify-start">
            <IconComponent name="ExcelExportIcon" width="1.25rem" height="1.25rem" className="me-1" />
            <Text>{t("Export")}</Text>
          </span>
        </ButtonComponent>
      </div>
    );

    const exportPDFBtnTemplate = () => (
      <div className="me-[1.875rem] bg-transparent">
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="pdf_export">
          <span className="flex flex-row items-center justify-start">
            <IconComponent name="PdfExport" width="1.25rem" height="1.25rem" className="me-1" />
            <Text>{t("PDF Export")}</Text>
          </span>
        </ButtonComponent>
      </div>
    );

    const toolbar = useMemo(
      () =>
        getIssuesInReviewToolbar({
          addBtnTemplate,
          removeBtnTemplate,
          exportBtnTemplate,
          exportPDFBtnTemplate,
        }),

      [disableAddIssues],
    );

    const closeStatusHistory = () => {
      setStatusHistoryModal({ show: false });
    };

    const standaloneIssueCreationCallback = () => {
      if (showIssuesInTabularForm) {
        refreshData(true, true, true);
      }
    };

    const openStatusHistoryFromData = (rowData: IssueInReviewRow) => {
      // Same passthrough gap as in `IssueStatusDropdown`: the entries carry the full status object.
      const { statuses } = rowData as { statuses: ResourceStatusEntry[] };
      if (statuses?.length > 1) {
        setStatusHistoryModal({ show: true, statuses });
      }
    };

    const openIssueInViewer = (e: MouseEvent | PointerEvent) => {
      const target = e.currentTarget as HTMLElement;
      const fileVersionId = target.dataset.fileversionid;
      const issueId = target.dataset.issueid;

      if (fileVersionId) {
        const urlData = {
          fileVersionId,
          issueId,
          spaceId,
          projectId,
        };
        const location = getViewerUrl(urlData);
        window.open(location, "_blank");
      }
    };

    const openIssueProperties = (selectedRecord: IssueInReviewRow) => {
      const { issueId: id, details } = selectedRecord;
      const fileVersionId = details.fileVersionId;
      const data = {
        id,
        spaceId,
        fileVersionId,
        projectUniqueId: projectId,
      };

      setIssueProperties({ show: true, data: [data] });
    };

    const closeIssueProperties = () => {
      setIssueProperties({ show: false, data: null });
    };

    const closeAddIssueModal = () => {
      setAddIssueModal(defaultAddIssueState);
      dispatch(
        setModalStatus({
          ...HoopsModalStatus,
          Issues: {
            ...HoopsModalStatus.Issues,
            currentStage: "default",
            backStage: "default",
            defaultStage: "issues",
          },
        }),
      );
      setSelectedIssue(null);
    };

    const createStandaloneIssue = () => {
      setAddIssueModal((prev) => ({
        ...prev,
        show: true,
        type: "create_standalone_issue",
        selectedIssues: [],
        newIssueDetails: {},
      }));
    };

    const addIssuesInReviewCallback = () => {
      setAddIssueModal((prev) => ({
        ...prev,
        show: true,
        type: "import_existing_issues",
        selectedIssues: [],
        newIssueDetails: {},
      }));
    };

    const closeRemoveIssueModal = () => {
      setRemoveIssueModal({ show: false, resources: [] });
    };

    const removeIssuesFromReview = (selectedRecords: IssueInReviewRow[]) => {
      const resources = selectedRecords.map(({ issueId, resourceType }) => ({
        issueId,
        resourceType,
      }));

      setRemoveIssueModal({ show: true, resources });
    };

    const exportIssuesToExcel = async () => {
      if (!dataGrid.current) return;
      dataGrid.current.toolbarModule?.enableItems(["excel_export"], false);
      const totalItems = dataGrid.current?.dataView?.getItemCount?.() ?? 0;
      const payload = {
        value: {
          take: totalItems || 1000,
          requiresCounts: true,
          skip: 0,
          params: {
            resourceTypes: ["RESOURCE_TYPE_ISSUE"],
            reviewId,
          },
        },
      };
      try {
        const allIssuesInReviewResponse = await getResourcesInReview(payload);
        const allIssuesInReview = (allIssuesInReviewResponse.data.result ?? []).map(
          ({ details, statuses, addedBy, addedDate }) => {
            const { title, officialResponse, fileName, severity, fileVersionNumber } = details;
            return {
              title,
              officialResponse: htmlToText(_.get(officialResponse, "title", "")),
              severity,
              addedBy,
              addedDate,
              file: fileName || "-",
              fileVersionNumber: fileVersionNumber ? `V${fileVersionNumber}` : "-",
              status: statuses.slice(-1)[0]?.name || "-",
            };
          },
        );
        if (allIssuesInReview.length) {
          const { default: ExcelJS } = await import("exceljs");
          const { saveAs } = await import("file-saver");
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet("Issues");
          const columns = [
            { header: t("Title"), key: "title", width: 30 },
            { header: t("Official Response"), key: "officialResponse", width: 35 },
            { header: t("Severity"), key: "severity", width: 20 },
            { header: t("File"), key: "file", width: 30 },
            { header: t("File Version"), key: "fileVersionNumber", width: 20 },
            { header: t("Status"), key: "status", width: 20 },
          ];
          worksheet.columns = columns;
          allIssuesInReview.forEach((row) => worksheet.addRow(row));
          const buffer = await workbook.xlsx.writeBuffer();
          saveAs(new Blob([buffer]), "IssuesInReview.xlsx");
        } else {
          dispatch(
            showToastNotification({
              content: t("There are no issues in this review"),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      } finally {
        dataGrid.current?.toolbarModule?.enableItems(["excel_export"], true);
      }
    };

    const exportIssuesToPDFConfirm = (paperSize: PaperSize) => {
      setShowExportPdfOptions(false);
      exportPdf({ reviewId, paperSize, exportType: EXPORT_TYPE.PDF });
    };

    const exportIssuesToPDF = async () => {
      if (!dataGrid.current) return;
      const itemCount = dataGrid.current?.dataView?.getItemCount?.() ?? 0;
      if (itemCount > 0) {
        setShowExportPdfOptions(true);
      } else {
        dispatch(
          showToastNotification({
            content: t("There are no issues in this review"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    };
    const toolbarClickHandler = useCallback(
      (toolClicked: string) => {
        if (!dataGrid.current) return;
        const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
        if (toolClicked === "remove_issue" && selectedRecords.length >= 1) {
          removeIssuesFromReview(selectedRecords);
        }
        if ((toolClicked === "issue_properties" || toolClicked === "properties") && selectedRecords.length === 1) {
          openIssueProperties(selectedRecords[0]!);
        }
        if (toolClicked === "excel_export") {
          exportIssuesToExcel();
        }
        if (toolClicked === "pdf_export") {
          exportIssuesToPDF();
        }
      },

      [reviewId, spaceId, projectId],
    );

    const selectionEvents = () => {
      if (dataGrid?.current) {
        const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];

        const removeBtnState = selectedRecords.length >= 1;

        dataGrid.current.toolbarModule?.enableItems(["remove_issue"], removeBtnState);
        setPropertiesBtnDisabled(selectedRecords.length !== 1);

        if (selectedRecords.length !== 1) {
          closeIssueProperties();
        }
      }
    };

    const dataBound = (ready: boolean) => {
      setIsLoadingComplete(ready);
    };

    const issueSelectionCallback = (issues: IssueItem[]) => {
      const resourceType = "RESOURCE_TYPE_ISSUE";
      issues = issues.filter(Boolean);
      const issuesListForReview = issues.map(({ id: issueId }) => ({
        issueId,
        resourceType,
      }));
      const issueIds = issues.map(({ id }) => id);

      viewingIssuesThroughIssueCards
        ? setAddIssueModal((prev) => ({
            ...prev,
            selectedIssues: issues,
            issueIds,
          }))
        : setAddIssueModal((prev) => ({
            ...prev,
            selectedIssues: issuesListForReview,
          }));
    };

    const removeIssuesFromReviewHandler = async () => {
      setIsLoadingRemoveIssues(true);
      const { resources } = removeIssueModal;
      const response = await removeResourcesFromReview({ reviewId, resources });
      setIsLoadingRemoveIssues(false);
      closeRemoveIssueModal();

      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("The selected issue(s) have been removed from the review"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );

        refreshData(true, true, true);

        return;
      }

      const content = response.data.errorMessage
        ? untranslated(response.data.errorMessage)
        : untranslated("The issue(s) could not be removed from the review");
      dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    };

    useEffect(() => {
      if (issuesAttachedToForm !== undefined) {
        fetchIssuesDataInForm();
        setIssuesAttachedToForm(undefined);
      }
    }, [issuesAttachedToForm, fetchIssuesDataInForm]);

    const handleToastNotification = (response: { data: { status?: boolean; errorMessage?: string } }) => {
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("The selected issue(s) have been added to the review"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        if (showIssuesInTabularForm) {
          refreshData(true, true, true);
        }

        return;
      }

      // KNOWN BUG, preserved verbatim: neither the server `errorMessage` nor the English fallback goes
      // through `untranslated()`/`t()` here (every sibling toast in this file does), so this toast is
      // never translated. The cast is erased; adding a call would change the emit.
      const content = (response.data.errorMessage ||
        "The issue(s) could not be added to the review") as TranslatedString;
      dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    };

    const attachIssuesToReviewCallback = async () => {
      setIsLoadingAttachIssues(true);
      const resources = addIssueModal.selectedIssues;

      try {
        if (showIssuesInTabularForm) {
          const response = await addResourcesToReview({ reviewId, resources });
          handleToastNotification(response);
        } else {
          // Only reached on the forms path, where `issueSelectionCallback` has already set `issueIds`.
          const ids = addIssueModal.issueIds!;
          await dispatch(addFormIdToIssues(ids, formId));
          setIssuesAttachedToForm("successful");
        }
      } catch {
        dispatch(
          showToastNotification({
            content: SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      } finally {
        setIsLoadingAttachIssues(false);
        closeAddIssueModal();
      }
    };

    useEffect(() => {
      if (dataGrid?.current && isLoadingComplete) {
        const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
        const removeBtnState = selectedRecords.length >= 1;

        dataGrid.current.toolbarModule?.enableItems(["remove_issue"], removeBtnState);
      }
    }, [isLoadingComplete, showProperties, dataGrid]);

    const _dropdownItemTemplate = (args: ReviewStatusItem) => {
      const style = getStatusBtnStyle(args.category);

      return (
        <div className="py-2 px-3">
          <span style={style} className="rounded py-1 px-4 text-sm text-white">
            {args.name}
          </span>
        </div>
      );
    };

    const IssueStatusDropdown = ({ args }: { args: IssueInReviewRow }) => {
      // `resourceInReviewItemSchema.statuses` declares only `name`; `_id`/`category` arrive via its
      // passthrough (SERVER SCHEMA GAP, reported). The assertion is erased.
      const status = args.statuses?.slice(-1)[0] as IssueStatusSelection | undefined;
      const [localStatusOptions, setLocalStatusOptions] = useState<ReviewStatusItem[]>(
        statusOptionsCache[args.issueId!] || [],
      );

      useEffect(() => {
        if (statusOptionsCache[args.issueId!]) {
          setLocalStatusOptions(statusOptionsCache[args.issueId!] ?? []);
          return;
        }
        let cancelled = false;
        const fetchOptions = async () => {
          const resource = fileStatusHelper(args);
          const options: ReviewStatusItem[] = await fetchStatusOptionsForIssue(args.issueId!, resource);
          if (!cancelled) {
            setLocalStatusOptions(Array.isArray(options) ? options : []);
          }
        };
        fetchOptions();
        return () => {
          cancelled = true;
        };
      }, [args.issueId]);

      const selectedOption = localStatusOptions.find((opt: ReviewStatusItem) => opt._id === status?._id) || null;

      return (
        <div className="flex flex-row items-center gap-4" onClick={(e) => e.stopPropagation()}>
          <AutoComplete
            options={localStatusOptions}
            valueField="_id"
            width={"12.5rem"}
            openOnFocus
            getOptionLabel={(option: ReviewStatusItem) => option?.name || ""}
            value={selectedOption}
            onChange={(_event: React.SyntheticEvent, newValue: ReviewStatusItem | null) => {
              if (newValue) {
                issueStatusChanged(newValue, status, args.issueId!, args.resourceType!);
              }
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ReviewStatusItem) => (
              <li {...props} style={{ ...props.style, paddingInlineStart: "0.25rem", paddingInlineEnd: "0.25rem" }}>
                <div className="py-2 px-1">
                  <span
                    style={getStatusBtnStyle(option.category)}
                    className="whitespace-nowrap rounded py-1 px-4 text-sm text-white"
                  >
                    {option.name}
                  </span>
                </div>
              </li>
            )}
            renderInput={(params: AutocompleteRenderInputParams) => {
              if (selectedOption) {
                params.InputProps = {
                  ...params.InputProps,
                  startAdornment: (
                    <ToolTipComponent position="top" content={selectedOption.name as TranslatedString}>
                      <span
                        style={getStatusBtnStyle(selectedOption.category)}
                        className="truncate rounded px-3 py-0.5 text-sm text-white max-w-full cursor-pointer"
                        onClick={(e) => {
                          const input = e.currentTarget.closest(".MuiAutocomplete-root")?.querySelector("input");
                          if (input) {
                            input.focus();
                            input.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
                          }
                        }}
                      >
                        {selectedOption.name}
                      </span>
                    </ToolTipComponent>
                  ),
                };
                params.inputProps = {
                  ...params.inputProps,
                  style: {
                    ...params.inputProps?.style,
                    opacity: 0,
                    width: 0,
                    minWidth: 0,
                    padding: 0,
                    position: "absolute",
                  },
                };
              }
              return <TextFieldComponent {...params} />;
            }}
            onKeyDown={(e) => e.stopPropagation()}
            slotProps={{ listbox: { sx: { minWidth: "max-content" } } }}
            sx={{ flexGrow: 1, cursor: "pointer", "& .MuiInputBase-root": { cursor: "pointer" } }}
          />

          {args.statuses?.length > 1 ? (
            <DetailsIcon
              size="24"
              color="#192048"
              onClick={() => openStatusHistoryFromData(args)}
              className="cursor-pointer"
            />
          ) : null}
          {args.statuses?.length === 1 ? (
            <ToolTipComponent position="left" showTipPointer content={t("There is no status history for this issue")}>
              <DetailsIcon
                size="20"
                color="#192048"
                className="pointer-events-none cursor-not-allowed opacity-50"
                onClick={() => {}}
              />
            </ToolTipComponent>
          ) : null}
        </div>
      );
    };

    const issueStatusFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: IssueInReviewRow,
    ) => {
      const container = document.createElement("div");
      portalCreateRoot(container).render(<IssueStatusDropdown args={dataContext} />);
      return container;
    };

    const issueImageFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: IssueInReviewRow,
    ) => {
      const imageUrl = getUrlForImage({ id: dataContext.issueId }, "IssueImage");
      const defaultImage = `${getImageUrl()}/Issues_default_image.svg`;

      const container = document.createElement("div");
      container.className = "w-[7.5rem] h-20 overflow-hidden rounded bg-[#f0f5f9] flex items-center justify-center";

      const img = document.createElement("img");
      img.src = imageUrl;
      img.className = "w-full h-full object-cover";
      img.alt = "snapshot";
      img.onerror = () => {
        img.src = defaultImage;
        img.onerror = null;
      };
      container.appendChild(img);
      return container;
    };

    const officialResponseFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: IssueInReviewRow,
    ) => {
      const container = document.createElement("div");
      portalCreateRoot(container).render(
        <SimpleEditor
          value={sanitizeHtml(dataContext?.details?.officialResponse?.title)}
          showToolbar={false}
          showCharCount={true}
          editable={false}
        />,
      );
      return container;
    };

    const openIssueInViewerFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: IssueInReviewRow,
    ) => {
      const { details } = dataContext;
      const { fileVersionId } = details || {};

      if (fileVersionId) {
        const container = document.createElement("div");
        container.className = "cursor-pointer";
        container.dataset.fileversionid = fileVersionId;
        container.dataset.issueid = dataContext.issueId;
        container.innerHTML = `<img src="${OpenIssueInViewerIcon}" alt="open issue in viewer" />`;
        container.onclick = openIssueInViewer;
        return container;
      }

      return "";
    };

    const onViewIssueClick = (args: string) => {
      const issueSelected = issues[args];
      setSelectedIssue(issueSelected);
    };

    const issuesGridHeader = () => <Text className="w-full text-2xl font-bold rounded-t-2">{t("Add Issues")}</Text>;

    const disabledIssueIds = viewingIssuesThroughIssueCards ? disabledIssueIdsInForms : reviewData.issueIds;
    const fileVersionIds = viewingIssuesThroughIssueCards ? null : reviewData.fileVersionIds;

    const issuesGridContent = useCallback(
      () => (
        <div className="flex flex-col h-600 w-full overflow-auto">
          <IssuesGrid
            showCheckboxes
            showStatusCol={false}
            showTitleLine={false}
            allowOpenSidebar={false}
            spaceId={spaceId}
            getAllIssuesInSpace
            allowTools={false}
            disabledIssueIds={disabledIssueIds}
            fileVersionIds={fileVersionIds}
            issueSelectionCallback={issueSelectionCallback}
            getStandaloneIssuesForSpaceId={spaceId}
            disabledViews
          />
        </div>
      ),

      [disabledIssueIds],
    );

    const issuesGridFooter = () => {
      const hasSelectedIssues = addIssueModal.selectedIssues.length;

      return (
        <div className="flex flex-row gap-4 items-center justify-start">
          <ButtonComponent
            onClick={attachIssuesToReviewCallback}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            loading={isLoadingAttachIssues}
            disabled={!hasSelectedIssues}
          >
            <Text>{t("Add Issues")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeAddIssueModal}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      );
    };

    const removeIssueModalHeader = () => (
      <Text className="w-full text-2xl font-bold rounded-t-2">{t("Remove Issue(s)")}</Text>
    );

    const removeIssueModalContent = () => (
      <Text className="pt-4">{t("Do you want to remove the selected issues from the review ?")}</Text>
    );

    const removeIssueModalFooter = () => (
      <div className="flex flex-row gap-4 items-center justify-start">
        <ButtonComponent
          onClick={removeIssuesFromReviewHandler}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={isLoadingRemoveIssues}
        >
          <Text>{t("Confirm")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeRemoveIssueModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
    const handleSelectedIssues = (issueId: string, deleteAll: boolean = false) => {
      if (deleteAll) {
        setSelectedIssuesInCards({});
      } else {
        const tempSelectedIssues: Record<string, boolean> = { ...(selectedIssuesInCards as Record<string, boolean>) };

        if (tempSelectedIssues[issueId]) {
          delete tempSelectedIssues[issueId];
        } else {
          tempSelectedIssues[issueId] = true;
        }
        setSelectedIssuesInCards(tempSelectedIssues);
      }
    };

    const standaloneIssueModal = () => (
      <CreateIssueModal
        closeModal={closeAddIssueModal}
        extraParametersOnIssueCreation={{ formId: formId ? [formId] : undefined }}
        attachedReviewId={reviewId}
        defaultSpaceId={spaceId}
        projectId={projectId}
        refreshDataGrid={standaloneIssueCreationCallback}
      />
    );

    const columnDefinitions = useMemo(
      () => [
        {
          id: "openInViewer",
          field: "",
          name: "",
          minWidth: 50,
          maxWidth: 50,
          formatter: openIssueInViewerFormatter,
          excludeFromExport: true,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
          excludeFromColumnChooser: true,
        },
        {
          id: "snapshot",
          field: "snapshot",
          name: t("Image"),
          minWidth: 130,
          formatter: issueImageFormatter,
          excludeFromExport: true,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "title",
          field: "title",
          name: t("Title"),
          minWidth: 200,
          _valueGetter: (params: IssueInReviewRow) => params.details?.title,
          clipMode: "EllipsisWithTooltip",
          filterType: "Text",
        },
        {
          id: "officialResponse",
          field: "officialResponse",
          name: t("Official Response"),
          minWidth: 250,
          _valueGetter: (params: IssueInReviewRow) => htmlToText(_.get(params.details?.officialResponse, "title", "")),
          formatter: officialResponseFormatter,
          filterType: "Text",
        },
        {
          id: "severity",
          field: "severity",
          name: t("Severity"),
          minWidth: 100,
          _valueGetter: (params: IssueInReviewRow) =>
            params?.details?.severity == "MAJOR" ? "Major" : params?.details?.severity == "MINOR" ? "Minor" : "-",
          filterType: "Text",
        },
        {
          id: "file",
          field: "file",
          name: t("File"),
          minWidth: 150,
          _valueGetter: (params: IssueInReviewRow) => params.details?.fileName || "-",
          clipMode: "EllipsisWithTooltip",
          filterType: "Text",
        },
        {
          id: "fileVersion",
          field: "fileVersion",
          name: t("File Version"),
          minWidth: 100,
          _valueGetter: (params: IssueInReviewRow) =>
            params.details?.fileVersionNumber ? `V${params.details.fileVersionNumber}` : "-",
          filterType: "Text",
        },
        {
          id: "status",
          field: "status",
          name: t("Status"),
          minWidth: 300,
          _valueGetter: (params: IssueInReviewRow) => params.statuses?.slice(-1)[0]?.name || "",
          formatter: issueStatusFormatter,
          filterType: "Text",
        },
      ],

      [reviewData, reviewId],
    );

    const getDataGrid = () => (
      <div
        className={
          isLoadingComplete
            ? "h-full w-full overflow-auto flex flex-col"
            : "h-full w-full overflow-auto flex flex-col invisible"
        }
      >
        <SlickGrid1
          ref={dataGrid}
          remoteDataSource={getResourcesInReviewUrl}
          query={issuesQuery}
          toolbar={toolbar}
          toolbarClickHandler={toolbarClickHandler}
          columnDefinitions={
            columnDefinitions as ColumnDefinition<IssueInReviewRow>[] as ValidatedColumnDefinition<IssueInReviewRow>[]
          }
          onSelectedRowIdsChanged={selectionEvents}
          setGridReady={dataBound}
          rowHeight={50}
          gridHeight="100%"
          enableColumnChooser
          infiniteScroll
          enableFilteringOption
          enableSortingOption
          enableGroupingOption
          excelExport
          hideNoDataIllustration
          enableSelectAllCheckbox
          enableRowResize
          // CUBE-21933: rows from `getResourcesInReview` carry `issueId`, not `_id`
          // — without `idMapping`, `ensureIds` falls back to a per-load random id
          // and selection breaks across renders, making remove/PDF actions fail to
          // resolve any selected rows.
          idMapping="issueId"
          propertiesPanel={showProperties}
          propertiesDisabled={propertiesBtnDisabled}
        />
      </div>
    );

    return (
      <div className="flex flex-row w-full h-full overflow-auto">
        {showExportPdfOptions && (
          <ExportPdfOptionsModal
            isVisible
            onExport={exportIssuesToPDFConfirm}
            onCancel={() => setShowExportPdfOptions(false)}
          />
        )}
        {showIssuesInTabularForm && getDataGrid()}
        {viewingIssuesThroughIssueCards && (
          <div className="pt-1 w-full">
            <div>{Object.keys(selectedIssuesInCards).length ? removeBtnTemplateInIssueCards() : addBtnTemplate()}</div>

            <div className="overflow-y-auto pt-6">
              {Object.keys(issues).length
                ? Object.values(issues).map((issue: IssueItem | undefined) => {
                    // The redux map is `Record<string, IssueItem | undefined>`; every value written by
                    // `setIssuesData` is present. `!` is erased.
                    const currentViewIssueId = issue!.id;

                    return (
                      <div key={currentViewIssueId} className="py-2 cursor-pointer">
                        <IssueCard
                          onViewIssueClick={onViewIssueClick}
                          handleSelectedIssues={handleSelectedIssues}
                          selectedIssues={selectedIssuesInCards as Record<string, boolean>}
                          key={currentViewIssueId}
                          currentViewIssueId={currentViewIssueId}
                          showAnimation={false}
                        />
                      </div>
                    );
                  })
                : "No Issues"}
            </div>
          </div>
        )}
        {signatureModal.show && (
          // `defaultSignatureModalState` seeds this state, so `reviewId` is `null` and both statuses are
          // `{}` at rest — shapes `ChangeFileStatusModalProps` (reviewId: string, statusTo: Status) cannot
          // express. The assertion is erased and the props passed are unchanged.
          <ChangeFileStatusModal
            {...(signatureModal as ComponentProps<typeof ChangeFileStatusModal> & { show: boolean })}
            closeModal={closeSignatureModal}
          />
        )}
        {issueProperties.show && showProperties && (
          <IssueDetails allowCloseBtn rowData={issueProperties.data!} closePropertiesCallback={closeIssueProperties} />
        )}
        {selectedIssue && (
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
            <div className="bg-white p-4 rounded-lg shadow-lg min-w-[62.5rem] ">
              <ViewIssue
                currentViewIssueId={selectedIssue.id}
                issue={selectedIssue}
                spaceId={spaceId}
                closeAddIssueModal={closeAddIssueModal}
                refreshData={() => refreshData(true, true, true)}
                isStandalone
                handleSelectedIssues={handleSelectedIssues}
              />
              <div className=" m-4">
                <ButtonComponent onClick={closeAddIssueModal} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                  <Text>{t("Close")}</Text>
                </ButtonComponent>
              </div>
            </div>
          </div>
        )}

        {addIssueModal.show && addIssueModal.type === "import_existing_issues" && (
          <ModalStateDialogComponent
            isVisible
            allowModalPadding={false}
            width="62.5rem"
            headerTitle={issuesGridHeader()}
            onCancel={closeAddIssueModal}
            modelContent={issuesGridContent()}
            footerContent={issuesGridFooter()}
          />
        )}
        {addIssueModal.show && addIssueModal.type === "create_standalone_issue" && standaloneIssueModal()}

        {HoopsModalStatus?.Issues?.currentStage == "edit_issue" && standaloneIssueModal()}

        {removeIssueModal.show && (
          <ModalStateDialogComponent
            isVisible
            allowModalPadding={false}
            width="31.25rem"
            headerTitle={removeIssueModalHeader()}
            modelContent={removeIssueModalContent()}
            footerContent={removeIssueModalFooter()}
            onCancel={closeRemoveIssueModal}
          />
        )}
        {statusHistoryModal.show && (
          <ResourceStatusHistoryInReview
            statuses={statusHistoryModal.statuses!}
            closeModal={closeStatusHistory}
            showSignatureHistory={false}
          />
        )}
      </div>
    );
  },
);
IssuesInReview.displayName = "IssuesInReview";
export default IssuesInReview;
